import { useMemo, useState } from 'react';
import type { CharacterBenefits } from '../../hooks/useCharacterBenefits';
import type { PlaySession } from '../../stores/playSessionStore';
import { usePlaySessionStore } from '../../stores/playSessionStore';
import { getSpell } from '@character-creator/data';
import { useDiceRollerStore } from '../../stores/diceRollerStore';
import { calculateCritDamageNotation } from '@character-creator/core';
import type { CritStyle } from '@character-creator/core';
import { CheatSheet } from './CheatSheet';

interface Attack {
  name: string;
  bonus: string;
  damage: string;
  damageType: string;
}

interface SpellLists {
  cantrips: string[];
  knownSpells: string[];
  preparedSpells: string[];
}

interface CategorizedActions {
  bonusActions: Array<{ feature: { id: string; name: string; description: string } }>;
  reactions: Array<{ feature: { id: string; name: string; description: string } }>;
  other: Array<{ feature: { id: string; name: string; description: string } }>;
}

interface CombatFeatures {
  sneakAttack?: { dice: string } | null;
}

interface CombatLayoutCasterProps {
  benefits: CharacterBenefits;
  session: PlaySession | null;
  attacks: Attack[];
  spells: SpellLists;
  categorizedActions: CategorizedActions;
  combatFeatures: CombatFeatures;
  onUseSpellSlot: (level: string) => void;
  onRestoreSpellSlot: (level: string) => void;
  onSetConcentration: (spell: string | null) => void;
}

const ABILITY_LABELS: Record<string, string> = {
  strength: 'STR',
  dexterity: 'DEX',
  constitution: 'CON',
  intelligence: 'INT',
  wisdom: 'WIS',
  charisma: 'CHA',
};

type CasterTab = 'actions' | 'spells' | 'stats';

/**
 * Tab Within Tab Layout (Comp 2)
 * - Tabbed interface: Actions | Spells | Stats
 * - Spells get dedicated tab with full details
 * - Best for caster characters
 */
export function CombatLayoutCaster({
  benefits,
  session,
  attacks,
  spells,
  categorizedActions,
  combatFeatures,
  onUseSpellSlot,
  onRestoreSpellSlot,
  onSetConcentration,
}: CombatLayoutCasterProps) {
  const { triggerRoll } = useDiceRollerStore();
  const setCritStyle = usePlaySessionStore((s) => s.setCritStyle);
  const setCritActive = usePlaySessionStore((s) => s.setCritActive);
  const critStyle: CritStyle = session?.combatToggles.critStyle ?? 'standard';
  const critActive = session?.combatToggles.critActive ?? false;
  const [activeTab, setActiveTab] = useState<CasterTab>('actions');
  const [expandedSpell, setExpandedSpell] = useState<string | null>(null);

  // Helper to build d20 notation with modifier
  const rollD20 = (label: string, modifier: number) => {
    const sign = modifier >= 0 ? '+' : '';
    triggerRoll(`1d20${sign}${modifier}`, label);
  };

  // Calculate save modifier
  const getSaveMod = (ability: string) => {
    const abilityMod = benefits.modifiers[ability as keyof typeof benefits.modifiers];
    const isProficient = benefits.savingThrows.includes(ability as never);
    return isProficient ? abilityMod + benefits.proficiencyBonus : abilityMod;
  };

  // Build spell data by level
  const { spellsByLevel, totalSpells } = useMemo(() => {
    const allSpellIds = [...new Set([
      ...spells.cantrips,
      ...spells.knownSpells,
      ...spells.preparedSpells,
    ])];

    const spellsByLevel = new Map<number, Array<{
      id: string;
      name: string;
      school: string;
      concentration: boolean;
      ritual: boolean;
      description: string;
      castingTime: string;
      range: string;
      duration: string;
      components: string;
      level: number;
    }>>();

    allSpellIds.forEach((spellId) => {
      const spell = getSpell(spellId);
      if (spell) {
        const level = spell.level;
        if (!spellsByLevel.has(level)) {
          spellsByLevel.set(level, []);
        }
        spellsByLevel.get(level)!.push({
          id: spell.id,
          name: spell.name,
          school: spell.school,
          concentration: spell.concentration,
          ritual: spell.ritual,
          description: spell.description,
          castingTime: spell.castingTime,
          range: spell.range,
          duration: spell.duration,
          level: spell.level,
          components: [
            spell.components.verbal ? 'V' : '',
            spell.components.somatic ? 'S' : '',
            spell.components.material ? 'M' : '',
          ].filter(Boolean).join(', '),
        });
      }
    });

    // Sort spells alphabetically within each level
    spellsByLevel.forEach((spellList) => {
      spellList.sort((a, b) => a.name.localeCompare(b.name));
    });

    return { spellsByLevel, totalSpells: allSpellIds.length };
  }, [spells]);

  return (
    <div className="space-y-4">
      {/* Saving Throws - Always visible */}
      <div>
        <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-2 flex items-center gap-2">
          Saving Throws
          <div className="flex-1 h-px bg-background-elevated" />
        </div>
        <div className="grid grid-cols-6 gap-2">
          {Object.entries(ABILITY_LABELS).map(([ability, label]) => {
            const mod = getSaveMod(ability);
            const isProficient = benefits.savingThrows.includes(ability as never);
            return (
              <button
                key={ability}
                onClick={() => rollD20(`${label} Save`, mod)}
                className={`p-2 bg-background-card rounded-lg text-center transition-colors hover:bg-background-elevated
                  ${isProficient ? 'border-l-2 border-accent-gold' : ''}`}
              >
                <div className="text-[10px] text-text-muted uppercase">{label}</div>
                <div className={`font-mono text-lg font-bold ${isProficient ? 'text-accent-gold' : 'text-text-primary'}`}>
                  {mod >= 0 ? '+' : ''}{mod}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Crit Style Selector */}
      <div>
        <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-2 flex items-center gap-2">
          Critical Hits
          <div className="flex-1 h-px bg-background-elevated" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-background-elevated flex-1">
            {(['standard', 'perkins'] as const).map((style) => (
              <button
                key={style}
                onClick={() => setCritStyle(style)}
                className={`flex-1 py-2 px-3 text-xs font-display tracking-wide transition-colors
                  ${critStyle === style
                    ? 'bg-error/20 text-error'
                    : 'bg-background-elevated text-text-muted hover:text-text-secondary'
                  }`}
              >
                {style === 'standard' ? 'STANDARD' : 'PERKINS'}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-1.5 text-[10px] text-text-muted">
          {critStyle === 'standard' ? 'Double all damage dice on crit' : 'Max dice + normal roll on crit'}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-background-elevated">
        <button
          onClick={() => setActiveTab('actions')}
          className={`px-4 py-2 text-sm font-display transition-colors relative ${
            activeTab === 'actions'
              ? 'text-accent-gold border-b-2 border-accent-gold -mb-[2px]'
              : 'text-text-muted hover:text-text-secondary'
          }`}
        >
          Actions
          {attacks.length > 0 && (
            <span className="ml-1.5 text-[10px] bg-background-elevated px-1.5 py-0.5 rounded">
              {attacks.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('spells')}
          className={`px-4 py-2 text-sm font-display transition-colors relative ${
            activeTab === 'spells'
              ? 'text-class-wizard border-b-2 border-class-wizard -mb-[2px]'
              : 'text-text-muted hover:text-text-secondary'
          }`}
        >
          Spells
          {totalSpells > 0 && (
            <span className="ml-1.5 text-[10px] bg-background-elevated px-1.5 py-0.5 rounded">
              {totalSpells}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          className={`px-4 py-2 text-sm font-display transition-colors relative ${
            activeTab === 'stats'
              ? 'text-accent-bronze border-b-2 border-accent-bronze -mb-[2px]'
              : 'text-text-muted hover:text-text-secondary'
          }`}
        >
          Stats
        </button>
      </div>

      {/* Actions Tab */}
      {activeTab === 'actions' && (
        <div className="space-y-4">
          {/* Attacks */}
          {attacks.length > 0 && (
            <div>
              <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-2 flex items-center gap-2">
                Attacks
                <div className="flex-1 h-px bg-background-elevated" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {attacks.map((attack, index) => {
                  const attackNotation = `1d20${attack.bonus}`;
                  // Build base damage notation including sneak attack if enabled
                  let baseDamage = attack.damage;
                  if (session?.combatToggles.sneakAttackEnabled && combatFeatures.sneakAttack) {
                    baseDamage = `${attack.damage}+${combatFeatures.sneakAttack.dice}`;
                  }
                  // Apply crit transformation if active
                  const displayDamage = critActive
                    ? calculateCritDamageNotation(baseDamage, critStyle)
                    : baseDamage;
                  return (
                    <div
                      key={index}
                      className={`p-3 bg-background-card border rounded-lg ${
                        critActive ? 'border-error/50' : 'border-background-elevated'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-display text-sm text-text-primary">{attack.name}</span>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setCritActive(!critActive)}
                            className={`text-[10px] px-1.5 py-0.5 rounded font-display font-bold transition-all
                              ${critActive
                                ? 'bg-error text-white animate-pulse'
                                : 'bg-background-primary text-text-muted hover:bg-error/20 hover:text-error'
                              }`}
                          >
                            CRIT
                          </button>
                          <span className="text-[10px] px-1.5 py-0.5 bg-background-primary rounded text-text-muted">
                            Attack
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => triggerRoll(attackNotation, `${attack.name} Attack`)}
                          className="flex-1 p-2 bg-background-elevated rounded text-center
                            hover:bg-accent-bronze/20 transition-colors active:scale-[0.98]"
                        >
                          <div className="text-[10px] text-text-muted">To Hit</div>
                          <div className="font-mono text-lg font-bold text-accent-gold">{attack.bonus}</div>
                        </button>
                        <button
                          onClick={() => {
                            triggerRoll(displayDamage, `${attack.name} Damage`);
                          }}
                          className={`flex-1 p-2 bg-background-elevated rounded text-center
                            transition-colors active:scale-[0.98] ${
                              critActive ? 'hover:bg-error/30' : 'hover:bg-error/20'
                            }`}
                        >
                          <div className="text-[10px] text-text-muted">
                            {critActive ? 'Crit Damage' : 'Damage'}
                          </div>
                          <div className={`font-mono text-sm font-bold ${critActive ? 'text-error animate-pulse' : 'text-error'}`}>
                            {displayDamage}
                          </div>
                          <div className="text-[9px] text-text-muted">{attack.damageType}</div>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bonus Actions */}
          <div>
            <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-2 flex items-center gap-2">
              Bonus Actions
              {categorizedActions.bonusActions.length > 0 && (
                <span className="text-[10px] text-text-muted font-normal">
                  ({categorizedActions.bonusActions.length})
                </span>
              )}
              <div className="flex-1 h-px bg-background-elevated" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {categorizedActions.bonusActions.length === 0 ? (
                <div className="p-3 bg-background-card/50 border border-dashed border-background-elevated rounded-lg text-center text-text-muted text-sm">
                  No bonus actions
                </div>
              ) : (
                categorizedActions.bonusActions.map(({ feature }) => (
                  <div
                    key={feature.id}
                    className="p-3 bg-background-card border border-class-rogue/30 rounded-lg"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-display text-sm text-text-primary">{feature.name}</span>
                      <span className="text-[9px] px-1.5 py-0.5 bg-class-rogue/20 text-class-rogue rounded">
                        BA
                      </span>
                    </div>
                    <p className="text-[10px] text-text-muted line-clamp-2">{feature.description}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Reactions */}
          <div>
            <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-2 flex items-center gap-2">
              Reactions
              {categorizedActions.reactions.length > 0 && (
                <span className="text-[10px] text-text-muted font-normal">
                  ({categorizedActions.reactions.length})
                </span>
              )}
              <div className="flex-1 h-px bg-background-elevated" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {categorizedActions.reactions.length === 0 ? (
                <div className="p-3 bg-background-card/50 border border-dashed border-background-elevated rounded-lg text-center text-text-muted text-sm">
                  No reactions
                </div>
              ) : (
                categorizedActions.reactions.map(({ feature }) => (
                  <div
                    key={feature.id}
                    className="p-3 bg-background-card border border-class-monk/30 rounded-lg"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-display text-sm text-text-primary">{feature.name}</span>
                      <span className="text-[9px] px-1.5 py-0.5 bg-class-monk/20 text-class-monk rounded">
                        RX
                      </span>
                    </div>
                    <p className="text-[10px] text-text-muted line-clamp-2">{feature.description}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Combat Tips */}
          <CheatSheet benefits={benefits} />
        </div>
      )}

      {/* Spells Tab */}
      {activeTab === 'spells' && (
        <div className="space-y-4">
          {/* Spellcasting Stats */}
          {benefits.spellcasting && (
            <>
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-class-wizard/20 to-class-warlock/20 border border-class-wizard/30 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-[10px] text-text-muted uppercase">Spell DC</div>
                    <div className="font-mono text-xl font-bold text-class-wizard">{benefits.spellcasting.saveDC}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-text-muted uppercase">Attack</div>
                    <div className="font-mono text-xl font-bold text-class-wizard">+{benefits.spellcasting.attackBonus}</div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] text-text-muted uppercase">Ability</div>
                  <div className="text-sm font-display text-text-primary uppercase">
                    {benefits.spellcasting.ability.slice(0, 3)}
                  </div>
                </div>
              </div>

              {/* Concentration */}
              {session?.concentrationSpell && (
                <div className="p-3 bg-class-wizard/20 border border-class-wizard/50 rounded-lg flex justify-between items-center">
                  <div>
                    <div className="text-[10px] text-class-wizard uppercase font-display tracking-wider">Concentrating</div>
                    <div className="text-text-primary font-medium">{session.concentrationSpell}</div>
                  </div>
                  <button
                    onClick={() => onSetConcentration(null)}
                    className="px-3 py-1.5 text-xs border border-error text-error rounded-lg
                      hover:bg-error hover:text-white transition-colors font-display"
                  >
                    DROP
                  </button>
                </div>
              )}

              {/* Spell Slots */}
              <div>
                <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-3 flex items-center gap-2">
                  Spell Slots
                  <div className="flex-1 h-px bg-background-elevated" />
                </div>
                <div className="flex flex-wrap gap-4 justify-center">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((level) => {
                    const slot = session?.spellSlots[level];
                    if (!slot || slot.max === 0) return null;
                    const ordinal = level === '1' ? '1st' : level === '2' ? '2nd' : level === '3' ? '3rd' : `${level}th`;
                    const available = slot.max - slot.used;
                    return (
                      <div key={level} className="text-center">
                        <div className="text-[10px] text-text-muted mb-1 font-display">{ordinal}</div>
                        <div className="flex gap-1">
                          {Array.from({ length: slot.max }).map((_, i) => (
                            <button
                              key={i}
                              onClick={() =>
                                i < slot.used ? onRestoreSpellSlot(level) : onUseSpellSlot(level)
                              }
                              className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                                i < slot.used
                                  ? 'border-text-muted bg-transparent'
                                  : 'border-class-wizard bg-class-wizard shadow-sm shadow-class-wizard/50'
                              }`}
                              title={i < slot.used ? 'Restore slot' : 'Use slot'}
                            />
                          ))}
                        </div>
                        <div className="text-[9px] text-text-muted mt-1">
                          {available}/{slot.max}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Spell List */}
              <div className="space-y-4">
                {Array.from(spellsByLevel.keys()).sort((a, b) => a - b).map((level) => (
                  <div key={level}>
                    <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-2 flex items-center gap-2">
                      {level === 0 ? 'Cantrips (At Will)' : `Level ${level} Spells`}
                      <div className="flex-1 h-px bg-background-elevated" />
                    </div>
                    <div className="space-y-2">
                      {spellsByLevel.get(level)!.map((spell) => (
                        <div
                          key={spell.id}
                          className="bg-background-elevated border border-background-primary rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => setExpandedSpell(expandedSpell === spell.id ? null : spell.id)}
                            className="w-full p-3 text-left hover:bg-background-primary/50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm text-text-primary font-medium">{spell.name}</span>
                                {spell.concentration && (
                                  <span className="text-[9px] px-1.5 py-0.5 bg-class-wizard/20 text-class-wizard rounded font-display">
                                    CONC
                                  </span>
                                )}
                                {spell.ritual && (
                                  <span className="text-[9px] px-1.5 py-0.5 bg-accent-gold/20 text-accent-gold rounded font-display">
                                    RITUAL
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-text-muted capitalize">{spell.school}</span>
                                <span className="text-text-muted">{expandedSpell === spell.id ? '▼' : '▶'}</span>
                              </div>
                            </div>
                          </button>

                          {expandedSpell === spell.id && (
                            <div className="px-3 pb-3 border-t border-background-primary">
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 py-3 text-[10px]">
                                <div className="bg-background-card rounded p-2">
                                  <span className="text-text-muted uppercase block">Casting</span>
                                  <span className="text-text-secondary">{spell.castingTime}</span>
                                </div>
                                <div className="bg-background-card rounded p-2">
                                  <span className="text-text-muted uppercase block">Range</span>
                                  <span className="text-text-secondary">{spell.range}</span>
                                </div>
                                <div className="bg-background-card rounded p-2">
                                  <span className="text-text-muted uppercase block">Duration</span>
                                  <span className="text-text-secondary">{spell.duration}</span>
                                </div>
                                <div className="bg-background-card rounded p-2">
                                  <span className="text-text-muted uppercase block">Components</span>
                                  <span className="text-text-secondary">{spell.components}</span>
                                </div>
                              </div>
                              <p className="text-xs text-text-secondary leading-relaxed mb-3">
                                {spell.description}
                              </p>
                              <div className="flex gap-2">
                                {level > 0 && (
                                  <button
                                    onClick={() => {
                                      onUseSpellSlot(level.toString());
                                      if (spell.concentration) {
                                        onSetConcentration(spell.name);
                                      }
                                    }}
                                    disabled={!session?.spellSlots[level.toString()] ||
                                      session.spellSlots[level.toString()].used >= session.spellSlots[level.toString()].max}
                                    className="flex-1 px-4 py-2 text-xs bg-class-wizard text-white font-display
                                      rounded-lg hover:bg-class-wizard/80 transition-colors
                                      disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    CAST (Lvl {level})
                                  </button>
                                )}
                                {level === 0 && (
                                  <button
                                    onClick={() => {
                                      if (spell.concentration) {
                                        onSetConcentration(spell.name);
                                      }
                                    }}
                                    className="flex-1 px-4 py-2 text-xs bg-class-wizard text-white font-display
                                      rounded-lg hover:bg-class-wizard/80 transition-colors"
                                  >
                                    CAST CANTRIP
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-4">
          {/* Saving Throws */}
          <div>
            <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-2 flex items-center gap-2">
              Saving Throws
              <div className="flex-1 h-px bg-background-elevated" />
            </div>
            <div className="grid grid-cols-6 gap-2">
              {Object.entries(ABILITY_LABELS).map(([ability, label]) => {
                const mod = getSaveMod(ability);
                const isProficient = benefits.savingThrows.includes(ability as never);
                return (
                  <button
                    key={ability}
                    onClick={() => rollD20(`${label} Save`, mod)}
                    className={`p-2 bg-background-card rounded-lg text-center transition-colors hover:bg-background-elevated
                      ${isProficient ? 'border-l-2 border-accent-gold' : ''}`}
                  >
                    <div className="text-[10px] text-text-muted uppercase">{label}</div>
                    <div className={`font-mono text-lg font-bold ${isProficient ? 'text-accent-gold' : 'text-text-primary'}`}>
                      {mod >= 0 ? '+' : ''}{mod}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Additional stats could go here */}
        </div>
      )}
    </div>
  );
}
